import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { articles } from '@/mocks/articles';
import { trendingMovies } from '@/mocks/home';
import { movieList, tvList, animeList, varietyList } from '@/mocks/category';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'info' | 'related'>('info');

  const allItems = [...trendingMovies, ...movieList, ...tvList, ...animeList, ...varietyList];
  const item = allItems.find((i) => i.id === id);
  const article = articles.find((a) => a.id === id);

  const relatedItems = allItems
    .filter((i) => i.id !== id)
    .slice(0, 6);

  const isArticle = !!article;

  if (!item && !article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-500"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-100 mb-2">内容未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">您访问的内容可能已被移除或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const pageTitle = isArticle ? article.title : item!.title;
  const pageDescription = isArticle ? article.summary : item!.description;

  const schema = isArticle
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.summary,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.publishDate,
        image: article.coverImage,
        publisher: { '@type': 'Organization', name: '91影视', url: 'https://www.91yingshi.com' },
      }
    : {
        '@context': 'https://schema.org',
        '@type': 'Movie',
        name: item!.title,
        description: item!.description,
        image: item!.poster,
        datePublished: item!.updateTime,
      };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91yingshi.com' },
      {
        '@type': 'ListItem',
        position: 2,
        name: isArticle ? '影视资讯' : '影视详情',
        item: `https://www.91yingshi.com/article/${id}`,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={`91影视,${pageTitle},91影视在线观看,免费影视`}
        canonicalUrl={`https://www.91yingshi.com/article/${id}`}
        ogType="article"
        ogImage={isArticle ? article.coverImage : item!.poster}
        schema={{ ...schema, ...breadcrumbSchema } as Record<string, unknown>}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {isArticle ? (
            <article className="max-w-[960px] mx-auto px-4 md:px-6 lg:px-8 py-8">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
                <Link to="/" className="hover:text-foreground-200 transition-colors cursor-pointer">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link
                  to={`/category/${article.category === '电影资讯' ? 'movie' : article.category === '动漫资讯' ? 'anime' : article.category === '综艺资讯' ? 'variety' : 'tv'}`}
                  className="hover:text-foreground-200 transition-colors cursor-pointer"
                >
                  {article.category}
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-300 truncate max-w-[200px]">{article.title}</span>
              </nav>

              <header className="mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="text-foreground-600">|</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.publishDate}
                  </span>
                  <span className="text-foreground-600">|</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.readCount.toLocaleString()} 阅读
                  </span>
                  <span className="text-foreground-600">|</span>
                  <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-400">
                    {article.category}
                  </span>
                </div>
              </header>

              <div className="rounded-xl overflow-hidden mb-8">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-auto object-cover"
                />
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-500 border border-background-200/50"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="prose prose-invert max-w-none">
                {article.content.split('\n\n').map((paragraph, i) => (
                  <p
                    key={i}
                    className="text-sm text-foreground-300 leading-relaxed mb-4"
                  >
                    {paragraph.trim()}
                  </p>
                ))}
              </div>

              <div className="mt-12 p-6 bg-background-100 rounded-xl border border-background-200/50">
                <h3 className="text-base font-semibold text-foreground-100 mb-2">关于91影视</h3>
                <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                  91影视是国内领先的在线影视资源平台，提供海量高清电影、电视剧、综艺、动漫在线免费观看。无需注册，无需会员，打开即看。
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/"
                    className="text-xs text-primary-400 hover:text-primary-300 transition-colors cursor-pointer"
                  >
                    返回首页
                  </Link>
                  <Link
                    to="/about"
                    className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors cursor-pointer"
                  >
                    关于我们
                  </Link>
                </div>
              </div>
            </article>
          ) : (
            <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-8">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8">
                <Link to="/" className="hover:text-foreground-200 transition-colors cursor-pointer">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link
                  to={`/category/${item!.category || 'movie'}`}
                  className="hover:text-foreground-200 transition-colors cursor-pointer"
                >
                  {item!.category === 'movie' ? '电影' : item!.category === 'tv' ? '电视剧' : item!.category === 'anime' ? '动漫' : '综艺'}
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-300 truncate max-w-[200px]">{item!.title}</span>
              </nav>

              <div className="flex flex-col md:flex-row gap-8">
                <div className="md:w-72 flex-shrink-0">
                  <div className="aspect-[2/3] rounded-xl overflow-hidden bg-background-100">
                    <img
                      src={item!.poster}
                      alt={`${item!.title} 海报`}
                      title={`${item!.title} - 91影视`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading mb-1">
                    {item!.title}
                  </h1>
                  {'englishTitle' in item! && (
                    <p className="text-sm text-foreground-500 mb-4">{item!.englishTitle}</p>
                  )}

                  <div className="flex items-center gap-2 mb-6">
                    <span className="px-3 py-1 rounded-md bg-primary-500 text-white text-sm font-bold">
                      {item!.rating}
                    </span>
                    <span className="text-sm text-foreground-400">豆瓣评分</span>
                  </div>

                  <div className="grid grid-cols-2 gap-y-3 gap-x-6 mb-6">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-600 w-14">类型：</span>
                      <span className="text-xs text-foreground-300">{item!.genre}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-600 w-14">地区：</span>
                      <span className="text-xs text-foreground-300">{item!.region}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-600 w-14">年份：</span>
                      <span className="text-xs text-foreground-300">{item!.year}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-600 w-14">时长：</span>
                      <span className="text-xs text-foreground-300">{item!.duration}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-600 w-14">更新：</span>
                      <span className="text-xs text-foreground-300">{item!.updateTime}</span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-foreground-100 mb-2">剧情简介</h3>
                    <p className="text-sm text-foreground-400 leading-relaxed">{item!.description}</p>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button className="px-6 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer">
                      <i className="ri-play-circle-line mr-1.5"></i>
                      立即播放
                    </button>
                    <button className="px-6 py-2.5 rounded-lg bg-background-100 hover:bg-background-200 text-foreground-300 text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer">
                      <i className="ri-heart-line mr-1.5"></i>
                      收藏
                    </button>
                    <button className="px-6 py-2.5 rounded-lg bg-background-100 hover:bg-background-200 text-foreground-300 text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer">
                      <i className="ri-share-line mr-1.5"></i>
                      分享
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-12">
                <div className="flex items-center gap-6 border-b border-background-200/50 mb-6">
                  <button
                    onClick={() => setActiveTab('info')}
                    className={`pb-3 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      activeTab === 'info'
                        ? 'text-primary-400 border-b-2 border-primary-400'
                        : 'text-foreground-500 hover:text-foreground-300'
                    }`}
                  >
                    详细介绍
                  </button>
                  <button
                    onClick={() => setActiveTab('related')}
                    className={`pb-3 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      activeTab === 'related'
                        ? 'text-primary-400 border-b-2 border-primary-400'
                        : 'text-foreground-500 hover:text-foreground-300'
                    }`}
                  >
                    相关推荐
                  </button>
                </div>

                {activeTab === 'info' ? (
                  <div className="max-w-2xl">
                    <p className="text-sm text-foreground-400 leading-relaxed mb-4">{item!.description}</p>
                    <p className="text-sm text-foreground-400 leading-relaxed">
                      本片在91影视提供高清在线观看服务，支持多种画质选择。91影视致力于为用户提供流畅的观影体验，海量资源、更新及时、无需注册，随时随地畅享精彩内容。
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {relatedItems.map((related) => (
                      <Link
                        key={related.id}
                        to={`/article/${related.id}`}
                        className="group block cursor-pointer"
                      >
                        <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-background-100">
                          <img
                            src={related.poster}
                            alt={related.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                          />
                          <div className="absolute top-2 left-2">
                            <span className="px-2 py-0.5 rounded-md bg-primary-500 text-white text-xs font-medium">
                              {related.rating}
                            </span>
                          </div>
                        </div>
                        <h4 className="text-xs font-medium text-foreground-200 mt-2 group-hover:text-primary-400 transition-colors line-clamp-1">
                          {related.title}
                        </h4>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}